import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import cx from 'classnames';
import { useMeasure } from 'react-use';
import ChevronDownIcon from 'components/icons/ChevronDown';
import {
  useUiValue,
  setNationalNotificationHeight,
  setNationalNotificationStatus,
} from 'stores/uiContext';
import { useSpring, animated } from 'react-spring';
import CloseIcon from 'components/icons/Close';
import breakpoints from 'utils/breakpoints';
import { PromotionBannerPopUp } from 'components/components/molecules';
import getPromotionPopupSessionKey from 'utils/promotionPopupSessionKey';
import { getMobileOS } from 'utils/getMobileOS';
import {
  hasSessionCookie,
  setSessionCookie,
  shouldSuppressPromotionPopupForSession,
} from 'utils/promotionPopupSessionCookie';

const BannerContainer = styled.div`
  min-height: 3.73rem;
  padding-left: 1.375rem;
  padding-right: 0.875rem;
  @media ${breakpoints.md} {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
  }
`;

const HIDE_NATIONAL_BANNER = 'hideNationalPromoBanner=';

const isEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const isIOSSafari = () => {
  if (typeof navigator === 'undefined') return false;

  return (
    getMobileOS() === 'iOS' &&
    /Safari/i.test(navigator.userAgent) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(navigator.userAgent)
  );
};

const MetroSearchPromoBanner = ({ fields = {} }) => {
  let hideNotificationSession = false;
  if (typeof window !== 'undefined' && document) {
    hideNotificationSession =
      typeof document.cookie === 'string' &&
      document.cookie.includes(HIDE_NATIONAL_BANNER + 'true');
  }

  const [measureRef, { height }] = useMeasure();
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [showCompleteBanner, setShowCompleteBanner] = useState(true);
  const [showPromotionPopup, setShowPromotionPopup] = useState(false);
  const textAlignment = 'center'; // right  left  center
  const enablePromotionPopup = isEnabled(fields?.enablePopup);
  const shouldSyncVisualViewportTop = !isIOSSafari();
  const promotionPopupSessionKey = useMemo(() => {
    if (!fields?.displayBanner || !fields?.line2) return '';

    return getPromotionPopupSessionKey(fields);
  }, [fields]);

  const [firstTabPressed, setFirstTabPressed] = useState(false);

  useEffect(() => {
    const handleFirstTab = (e) => {
      if (e.key === 'Tab' && !firstTabPressed) {
        e.preventDefault();
        setFirstTabPressed(true);

        const skipToMain = document.getElementById('skip-to-main');
        if (skipToMain) {
          skipToMain.focus();
        }
      }
    };

    window.addEventListener('keydown', handleFirstTab);

    return () => {
      window.removeEventListener('keydown', handleFirstTab);
    };
  }, [firstTabPressed]);

  const [{ nationalNotificationActive }, dispatch] = useUiValue();
  const [visualViewportTop, setVisualViewportTop] = useState(0);

  const setBannerMeasureRef = useCallback(
    (node) => {
      bannerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const measureBannerHeight = useCallback(() => {
    if (!bannerRef.current) return;

    setBannerHeight(
      Math.ceil(bannerRef.current.getBoundingClientRect().height),
    );
  }, []);

  useEffect(() => {
    if (!shouldSyncVisualViewportTop) return undefined;

    if (typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    let rafId = 0;

    const syncVisualViewportTop = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const doc = document.documentElement;
        const maxScrollY = doc.scrollHeight - window.innerHeight;
        const y = window.scrollY;

        const atTop = y <= 0;
        const atBottom = y >= maxScrollY - 1;

        if (atTop || atBottom) {
          setVisualViewportTop(0);
          return;
        }

        setVisualViewportTop(Math.max(0, visualViewport.offsetTop || 0));
      });
    };

    visualViewport.addEventListener('resize', syncVisualViewportTop);
    visualViewport.addEventListener('scroll', syncVisualViewportTop);
    window.addEventListener('scroll', syncVisualViewportTop, {
      passive: true,
    });

    syncVisualViewportTop();

    return () => {
      cancelAnimationFrame(rafId);
      visualViewport.removeEventListener('resize', syncVisualViewportTop);
      visualViewport.removeEventListener('scroll', syncVisualViewportTop);
      window.removeEventListener('scroll', syncVisualViewportTop);
    };
  }, [shouldSyncVisualViewportTop]);

  useEffect(() => {
    if (
      fields &&
      fields.displayBanner &&
      fields.line2 &&
      !hideNotificationSession
    ) {
      dispatch(setNationalNotificationHeight(bannerHeight || height));
      dispatch(setNationalNotificationStatus(true));
    } else {
      dispatch(setNationalNotificationHeight(0));
      dispatch(setNationalNotificationStatus(false));
    }
  }, [
    bannerHeight,
    height,
    fields.displayBanner,
    fields.line2,
    hideNotificationSession,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !nationalNotificationActive ||
      !(fields && fields.displayBanner && fields.line2)
    ) {
      setBannerHeight(0);
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;

    const syncBannerHeight = () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);

      rafId = requestAnimationFrame(measureBannerHeight);
      timeoutId = window.setTimeout(measureBannerHeight, 250);
    };

    syncBannerHeight();
    window.addEventListener('resize', syncBannerHeight, { passive: true });
    window.addEventListener('orientationchange', syncBannerHeight);
    window.visualViewport?.addEventListener('resize', syncBannerHeight);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', syncBannerHeight);
      window.removeEventListener('orientationchange', syncBannerHeight);
      window.visualViewport?.removeEventListener('resize', syncBannerHeight);
    };
  }, [
    fields,
    height,
    measureBannerHeight,
    nationalNotificationActive,
    showCompleteBanner,
  ]);

  useEffect(() => {
    if (!enablePromotionPopup || !promotionPopupSessionKey) {
      setShowPromotionPopup(false);
      return;
    }

    try {
      setShowPromotionPopup(
        !shouldSuppressPromotionPopupForSession() &&
          !hasSessionCookie(promotionPopupSessionKey),
      );
    } catch {
      setShowPromotionPopup(true);
    }
  }, [enablePromotionPopup, promotionPopupSessionKey]);
  const fixedBarStyle = useSpring({
    height: bannerHeight || (height ? height : 'auto'),
    config: { duration: shouldSyncVisualViewportTop ? 100 : 0 },
    top: visualViewportTop,
  });

  const closeNotification = () => {
    dispatch(setNationalNotificationStatus(false));
    dispatch(setNationalNotificationHeight(0));
    document.cookie = HIDE_NATIONAL_BANNER + 'true; path=/;';
  };

  const closePromotionPopup = () => {
    if (promotionPopupSessionKey) {
      try {
        setSessionCookie(promotionPopupSessionKey);
      } catch {
        // Keep the popup closable if cookies are unavailable.
      }
    }

    setShowPromotionPopup(false);
  };

  const promotionPopup = (
    <PromotionBannerPopUp
      isOpen={showPromotionPopup}
      onClose={closePromotionPopup}
      promotionValues={fields}
    />
  );

  if (
    !nationalNotificationActive ||
    !(fields && fields.displayBanner && fields.line2)
  ) {
    return promotionPopup;
  }

  return (
    <>
      {promotionPopup}
      {ReactDOM.createPortal(
        <animated.div
          className={cx('z-40 bg-aqua fixed left-0 right-0', {
            /* "relative ": !fixedBarVisible, // || isMobile,
            "fixed top-0": fixedBarVisible, // && !isMobile,*/
          })}
          style={{
            ...fixedBarStyle,
            WebkitTransform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          }}
        >
          <BannerContainer
            ref={setBannerMeasureRef}
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
            className={cx(
              'bg-aqua w-screen flex items-start justify-between ',
              {
                absolute: !!height,
              },
            )}
            role={'region'}
            aria-label={'national notification'}
          >
            <div
              className={cx('w-full py-4 flex flex-col', {
                'justify-center': textAlignment === 'center',
                'justify-start': textAlignment === 'left',
                'justify-end': textAlignment === 'right',
              })}
            >
              {showCompleteBanner && fields.line1 && (
                <span
                  className={cx(
                    'text-promo-dark-blue font-graphie mr-4 w-full',
                    {
                      'text-center': textAlignment === 'center',
                      'text-left': textAlignment === 'left',
                      'text-right': textAlignment === 'right',
                    },
                  )}
                >
                  <RichText
                    tag='span'
                    className='richtext'
                    field={{ value: fields.line1 }}
                  ></RichText>
                </span>
              )}
              <div
                className={cx(
                  'text-promo-dark-blue font-trade-gothic-20 mr-4 w-full flex flex-col md:flex-row',
                  {
                    'items-center justify-center': textAlignment === 'center',
                    'items-left justify-start': textAlignment === 'left',
                    'items-right justify-end': textAlignment === 'right',
                  },
                )}
              >
                {fields.marketPromotionLogo &&
                  fields.marketPromotionLogo.src && (
                    <div className={cx(' mx-3', {})} aria-hidden='true'>
                      <div className='flex items-center justify-center mr-3 md:mt-0 h-16 mb-2 md:mb-0'>
                        <img
                          className='object-cover h-full'
                          src={fields.marketPromotionLogo.src}
                          alt={fields.marketPromotionLogo.alt}
                        />
                      </div>
                    </div>
                  )}
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: fields.line2 }}
                />
              </div>
              {showCompleteBanner && fields.line3 && (
                <span
                  className={cx(
                    'text-promo-dark-blue font-graphie mr-4 w-full',
                    {
                      'text-center': textAlignment === 'center',
                      'text-left': textAlignment === 'left',
                      'text-right': textAlignment === 'right',
                    },
                  )}
                >
                  <RichText
                    tag='span'
                    className='richtext'
                    field={{ value: fields.line3 }}
                    onKeyDown={(event) => {
                      if (event.key === 'Tab' && !event.shiftKey) {
                        event.preventDefault();
                        const firstHeaderFocusableElement =
                          document.getElementById('logo-index');
                        if (firstHeaderFocusableElement) {
                          firstHeaderFocusableElement.focus();
                        }
                      }
                    }}
                  ></RichText>
                </span>
              )}
            </div>
            <div className='flex flex-col-reverse h-full w-8'>
              <button
                id='national-notification-banner_index'
                className={cx('absolute bottom-0 pb-4 ', {
                  hidden: !(fields.line1 || fields.line3),
                })}
                aria-label='Notification banner'
                aria-expanded={showCompleteBanner}
                onClick={(e) => {
                  setShowCompleteBanner(!showCompleteBanner);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Tab' &&
                    !event.shiftKey &&
                    !fields.line1 &&
                    !fields.line2 &&
                    !fields.line3
                  ) {
                    event.preventDefault();
                    const firstHeaderFocusableElement =
                      document.getElementById('logo-index');
                    if (firstHeaderFocusableElement) {
                      firstHeaderFocusableElement.focus();
                    }
                  }
                }}
                onBlur={(e) => {
                  e.preventDefault();
                  const containers = [
                    document.getElementById('national-notification-banner'),
                    document.getElementById('market-notification-banner'),
                    document.getElementById('notification-banner'),
                  ];
                  if (containers) {
                    containers.forEach((container) => {
                      if (container) {
                        const focusableElements =
                          container.querySelectorAll('a, button');
                        focusableElements.forEach((element) => {
                          element.setAttribute('tabindex', '0');
                        });
                      }
                    });
                  }
                }}
              >
                <ChevronDownIcon
                  className={cx(
                    'w-5 h-5 ml-1 mt-1 text-mattamy-blue transform ',
                    {
                      'rotate-180': showCompleteBanner,
                      'rotate-270': !showCompleteBanner,
                    },
                  )}
                />
              </button>
              <button
                className='mt-4 hidden'
                onClick={closeNotification}
                aria-label='Close national promo banner'
              >
                <CloseIcon className='text-mattamy-blue w-6 h-6' />
              </button>
            </div>
          </BannerContainer>
        </animated.div>,
        document.getElementById('national-notification-banner'),
      )}
    </>
  );
};

export default MetroSearchPromoBanner;
